import React, { useRef } from "react";
import { toPng } from "html-to-image";
import { MY_CONFIG } from "@/constants";

interface InvoiceProps {
  order: any;
  closeDialog?: () => void;
}

const Invoice: React.FC<InvoiceProps> = ({ order, closeDialog }) => {
  const invoiceRef = useRef<HTMLDivElement>(null);

  const o = normalizeOrder(order);

  const formatDate = (ts: number) =>
    new Date(ts * 1000).toLocaleString("en-MV", {
      dateStyle: "medium",
      timeStyle: "short",
    });

  const isPaid = o.paymentConfirmed || o.status === "PAID";

  const downloadInvoice = async () => {
    if (!invoiceRef.current) return;
    const dataUrl = await toPng(invoiceRef.current, { cacheBust: true });
    const link = document.createElement("a");
    link.download = `invoice_${o.uid}.png`;
    link.href = dataUrl;
    link.click();
  };

  return (
    <div className="w-full flex justify-center p-4 bg-gray-100 print:bg-white">
      <div
        ref={invoiceRef}
        className="w-full max-w-md bg-white shadow-md rounded-lg p-4 font-mono"
      >
        {/* HEADER */}
        <div className="flex justify-between items-start border-b pb-4">
          <div>
            <h1 className="text-lg font-bold">{MY_CONFIG.OFFICIAL_NAME.en}</h1>
            <p className="text-xs">{MY_CONFIG.EMAIL}</p>

            <p className="mt-3 text-sm font-bold">INVOICE</p>
            <p className="text-xs">Order: {o.uid}</p>
            <p className="text-xs">Date: {formatDate(o.date)}</p>

            <p
              className={`mt-2 text-xs font-bold ${
                isPaid ? "text-green-600" : "text-red-600"
              }`}
            >
              {isPaid ? "PAYMENT CONFIRMED" : "PAYMENT PENDING"}
            </p>
          </div>

          {!isPaid && (
            <img
              className="w-32 h-32 object-contain"
              src={MY_CONFIG.QR_CODE}
              alt="QR"
            />
          )}
        </div>

        {/* CUSTOMER */}
        <div className="py-3 border-b text-xs">
          <p className="font-bold mb-1">CUSTOMER</p>
          <p>Name: {o.customer.name}</p>
          <p>Email: {o.customer.email}</p>
          <p>Phone: {o.customer.phone || "-"}</p>
          <p>Delivery: {o.customer.address || "-"}</p>
        </div>


        {/* <div className="">{JSON.stringify(o, null,2)}</div> */}

        {/* ITEMS */}
        <div className="py-3 border-b">
          <p className="font-bold text-xs mb-2">ITEMS</p>
          {o.items.map((item) => (
            <div key={item.id} className="mb-2">
              <div className="flex justify-between text-xs font-bold">
                <span>{item.name}</span>
                <span>MVR {item.total}</span>
              </div>
              <div className="flex justify-between text-[10px] text-gray-600">
                <span>Qty: {item.quantity}</span>
                <span>Price: {item.price}</span>
              </div>
              <div className="border-b border-dashed mt-1"></div>
            </div>
          ))}
        </div>

        {/* TOTALS */}
        <div className="py-3 border-b text-xs">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>MVR {o.totals.subtotal}</span>
          </div>
          <div className="flex justify-between">
            <span>Tax</span>
            <span>MVR {o.totals.tax}</span>
          </div>
          <div className="flex justify-between">
            <span>Delivery</span>
            <span>MVR {o.totals.deliveryFee}</span>
          </div>
          <div className="flex justify-between font-bold text-sm border-t pt-2 mt-2">
            <span>Total</span>
            <span>MVR {o.totals.final}</span>
          </div>
        </div>

        {/* FOOTER */}
        <div className="text-center text-[10px] text-gray-500 py-4">
          Thank you for your purchase
        </div>

        {/* ACTIONS */}
        <div className="flex gap-2 justify-center print:hidden">
          <button
            onClick={downloadInvoice}
            className="bg-green-600 text-white px-4 py-2 rounded text-xs"
          >
            Download
          </button>
          {closeDialog && (
            <button
              onClick={closeDialog}
              className="bg-red-600 text-white px-4 py-2 rounded text-xs"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Invoice;

function normalizeOrder(order: any) {
  const isDbOrder = !!order.order_id;

  return {
    uid: isDbOrder ? order.order_uid : order.entity?.id,
    date: isDbOrder
      ? order.created_at
      : Math.floor(Date.now() / 1000),

    paymentConfirmed: isDbOrder
      ? Number(order.order_payment_confirmed) === 1
      : false,

    status: isDbOrder
      ? order.order_status
      : "PENDING_PAYMENT",

    customer: {
      name: isDbOrder
        ? order.order_customer_name
        : order.customer?.name,
      email: isDbOrder
        ? order.order_customer_email
        : order.customer?.email,
      phone: isDbOrder
        ? order.order_customer_phone
        : order.customer?.phone,
      address: isDbOrder
        ? order.order_delivery_location
        : order.customer?.address,
    },

    totals: {
      subtotal: isDbOrder
        ? order.order_total_amount
        : order.totals?.subtotal,
      tax: isDbOrder
        ? order.order_tax
        : order.totals?.tax,
      deliveryFee: isDbOrder
        ? order.order_delivery_fee
        : order.delivery_fee ?? 0,
      final: isDbOrder
        ? order.order_final_amount
        : order.totals?.final_total,
    },

    items: isDbOrder
      ? order.items.map((i: any) => ({
          id: i.order_item_id,
          name: i.name,
          quantity: i.quantity,
          price: i.unit_price,
          total: i.total,
        }))
      : order.items.map((i: any, idx: number) => ({
          id: idx,
          name: i.name,
          quantity: i.quantity,
          price: i.price,
          total: i.price * i.quantity,
        })),
  };
}